import { defineStore } from 'pinia'
import type { Product } from './products'

interface CartItem extends Product {
    quantity: number
}

export const useCartStore = defineStore('cart', {
    state: () => ({
        items: [] as CartItem[],
        isHydrated: false
    }),

    getters: {
        totalItems: (state) => state.items.reduce((total, item) => total + item.quantity, 0),
        totalPrice: (state) => state.items.reduce((total, item) => total + (item.price * item.quantity), 0),
        isEmpty: (state) => state.items.length === 0
    },

    actions: {
        addItem(product: Product) {
            const existingItem = this.items.find(item => item.id === product.id)
            if (existingItem) {
                existingItem.quantity++
            } else {
                this.items.push({ ...product, quantity: 1 })
            }
            this.saveToLocalStorage()
        },

        removeItem(productId: string | number) {
            this.items = this.items.filter(item => item.id !== productId)
            this.saveToLocalStorage()
        },

        updateQuantity(productId: string | number, quantity: number) {
            const item = this.items.find(item => item.id === productId)
            if (item) {
                item.quantity = Math.max(0, quantity)
                if (item.quantity === 0) {
                    this.removeItem(productId)
                }
            }
            this.saveToLocalStorage()
        },

        clearCart() {
            this.items = []
            this.saveToLocalStorage()
        },

        saveToLocalStorage() {
            if (process.client) {
                localStorage.setItem('alarm_cart', JSON.stringify(this.items))
            }
        },

        initCart() {
            if (process.client && !this.isHydrated) {
                const saved = localStorage.getItem('alarm_cart')
                if (saved) {
                    try {
                        this.items = JSON.parse(saved)
                    } catch (e) {
                        console.error('Failed to parse cart from localStorage', e)
                    }
                }
                this.isHydrated = true
            }
        }
    }
})
